/**
 * ErrorBoundary - Crash isolation for React component trees
 *
 * Catches render errors in child components and displays a fallback UI
 * instead of crashing the entire application.
 *
 * @module @humanizer/studio/components/ui/ErrorBoundary
 */

import React from 'react';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** 'panel' for inline panel recovery, 'page' for full-page */
  fallback?: 'panel' | 'page';
  /** Called when the user clicks "Try Again" */
  onReset?: () => void;
  /** Label shown in the fallback UI */
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo.componentStack);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const variant = this.props.fallback ?? 'panel';
    const label = this.props.label ?? 'This section';

    return (
      <div className={`error-boundary error-boundary--${variant}`} role="alert">
        <div className="error-boundary__content">
          <div className="error-boundary__title">Something went wrong</div>
          <p className="error-boundary__message">
            {label} encountered an error and couldn't render.
          </p>
          {this.state.error && (
            <pre className="error-boundary__detail">
              {this.state.error.message}
            </pre>
          )}
          <button
            className="error-boundary__retry btn btn--secondary btn--sm"
            onClick={this.handleReset}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
